import { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, User, MessageSquare, Clock, Globe } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';

const API_URL = import.meta.env.PROD ? '/api' : 'http://localhost:3003/api';

interface ContactContent {
  title: string;
  subtitle: string;
  description: string;
  address: string;
  email: string;
  phone: string;
  map_embed_url: string;
}

const Contact = () => {
  const { toast } = useToast();
  const [content, setContent] = useState<ContactContent>({
    title: 'Contact Us',
    subtitle: 'Get in Touch',
    description: 'We would love to hear from you',
    address: '',
    email: '',
    phone: '',
    map_embed_url: ''
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const response = await fetch(`${API_URL}/contact-page`);
        const data = await response.json();
        setContent(data);
      } catch (error) {
        console.error('Failed to load contact page:', error);
      }
    };

    loadContent();

    // Listen for updates from admin panel
    window.addEventListener('contentUpdated', loadContent);
    return () => window.removeEventListener('contentUpdated', loadContent);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/contact-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({
          title: "Message sent!",
          description: "We'll get back to you as soon as possible.",
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(205,156,222,0.08),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(66,153,225,0.08),transparent_50%)]"></div>
        <div className="container mx-auto max-w-6xl text-center relative z-10">
          <h1 className="text-5xl md:text-7xl font-bold text-foreground mb-6 animate-fade-in bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            {content.title}
          </h1>
          <div className="w-24 h-1.5 bg-gradient-to-r from-primary to-secondary mx-auto mb-8 rounded-full animate-fade-in stagger-1"></div>
          <p className="text-2xl text-muted-foreground mb-4 animate-fade-in stagger-2 font-semibold">
            {content.subtitle}
          </p>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto animate-fade-in stagger-3 leading-relaxed">
            {content.description}
          </p>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-20 px-4 bg-gradient-to-b from-background via-primary/5 to-background">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {/* Email Card */}
            {content.email && (
              <Card className="group hover:shadow-2xl transition-all duration-500 border-2 hover:border-primary/50 hover:-translate-y-2 bg-gradient-to-br from-card to-card/50 backdrop-blur-sm animate-fade-in">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary via-primary/80 to-primary/60 rounded-2xl mb-5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <Mail className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-foreground group-hover:text-primary transition-colors">Email Us</h3>
                  <a 
                    href={`mailto:${content.email}`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors font-medium break-all"
                  >
                    {content.email}
                  </a>
                </CardContent>
              </Card>
            )}

            {/* Phone Card */}
            {content.phone && (
              <Card className="group hover:shadow-2xl transition-all duration-500 border-2 hover:border-secondary/50 hover:-translate-y-2 bg-gradient-to-br from-card to-card/50 backdrop-blur-sm animate-fade-in stagger-1">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-secondary via-secondary/80 to-secondary/60 rounded-2xl mb-5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <Phone className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-foreground group-hover:text-secondary transition-colors">Call Us</h3>
                  <a 
                    href={`tel:${content.phone}`}
                    className="text-sm text-muted-foreground hover:text-secondary transition-colors font-medium"
                  >
                    {content.phone}
                  </a>
                </CardContent>
              </Card>
            )}

            {/* Address Card */}
            {content.address && (
              <Card className="group hover:shadow-2xl transition-all duration-500 border-2 hover:border-primary/50 hover:-translate-y-2 bg-gradient-to-br from-card to-card/50 backdrop-blur-sm animate-fade-in stagger-2">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary via-primary/80 to-secondary/60 rounded-2xl mb-5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <MapPin className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-foreground group-hover:text-primary transition-colors">Visit Us</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                    {content.address}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Business Hours Card */}
            <Card className="group hover:shadow-2xl transition-all duration-500 border-2 hover:border-secondary/50 hover:-translate-y-2 bg-gradient-to-br from-card to-card/50 backdrop-blur-sm animate-fade-in stagger-3">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-secondary via-secondary/80 to-primary/60 rounded-2xl mb-5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-foreground group-hover:text-secondary transition-colors">Open Daily</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  24/7 Support<br/>
                  Always Here to Help
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form & Map Grid */}
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="group hover:shadow-2xl transition-all duration-500 border-2 hover:border-primary/30 bg-gradient-to-br from-card via-card to-primary/5 animate-slide-in-left overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-3xl -z-10"></div>
              <CardContent className="p-10 relative">
                <div className="mb-8">
                  <h2 className="text-4xl font-bold mb-3 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
                    Send us a message
                  </h2>
                  <p className="text-muted-foreground text-lg">Fill out the form below and we'll get back to you soon</p>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="group/input">
                    <label htmlFor="name" className="block text-sm font-semibold mb-2 text-foreground group-hover/input:text-primary transition-colors flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Your Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      className="w-full h-12 px-4 rounded-xl border-2 focus:border-primary focus:ring-4 focus:ring-primary/20 transition-all bg-background/50 backdrop-blur-sm"
                    />
                  </div>

                  <div className="group/input">
                    <label htmlFor="email" className="block text-sm font-semibold mb-2 text-foreground group-hover/input:text-primary transition-colors flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="w-full h-12 px-4 rounded-xl border-2 focus:border-primary focus:ring-4 focus:ring-primary/20 transition-all bg-background/50 backdrop-blur-sm"
                    />
                  </div>

                  <div className="group/input">
                    <label htmlFor="phone" className="block text-sm font-semibold mb-2 text-foreground group-hover/input:text-primary transition-colors flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone Number
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+30 123 456 7890"
                      className="w-full h-12 px-4 rounded-xl border-2 focus:border-primary focus:ring-4 focus:ring-primary/20 transition-all bg-background/50 backdrop-blur-sm"
                    />
                  </div>

                  <div className="group/input">
                    <label htmlFor="message" className="block text-sm font-semibold mb-2 text-foreground group-hover/input:text-primary transition-colors flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us how we can help you..."
                      className="w-full min-h-[180px] p-4 rounded-xl border-2 focus:border-primary focus:ring-4 focus:ring-primary/20 transition-all resize-none bg-background/50 backdrop-blur-sm"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-14 text-lg font-semibold rounded-xl bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_100%] hover:bg-right shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-500 group-hover:animate-gradient"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <><div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>Sending...</>
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Map */}
            {content.map_embed_url ? (
              <Card className="group hover:shadow-2xl transition-all duration-500 border-2 hover:border-secondary/30 overflow-hidden h-full min-h-[700px] animate-slide-in-right relative">
                <div className="absolute top-4 left-4 z-10 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg border border-border">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <MapPin className="h-4 w-4 text-primary" />
                    Our Location
                  </div>
                </div>
                <iframe
                  src={content.map_embed_url}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Location Map"
                  className="w-full h-full rounded-lg"
                ></iframe>
              </Card>
            ) : (
              <Card className="group hover:shadow-2xl transition-all duration-500 border-2 hover:border-secondary/30 overflow-hidden h-full min-h-[700px] animate-slide-in-right bg-gradient-to-br from-secondary/10 via-primary/5 to-background">
                <CardContent className="h-full flex flex-col items-center justify-center p-10 text-center">
                  <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-secondary/20 to-primary/20 rounded-full mb-6">
                    <Globe className="h-12 w-12 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-foreground">Location Map</h3>
                  <p className="text-muted-foreground max-w-md">
                    Map will be displayed here. Configure the map URL in the admin panel to show your exact location.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
